import React from 'react';
import { Sparkles, ArrowRight } from 'lucide-react';

/**
 * FilterAlternatives - Generic component for displaying clickable filter alternatives
 * 
 * Use Cases:
 * - Unmapped filters (e.g., "pink" color not available)
 * - Zero results with validated alternatives
 * - Any scenario where we want to offer users clickable alternative searches
 * 
 * Reusable by any agent, not just Product Agent
 */

interface FilterAlternative {
  id: string;
  description: string;
  count: number;
  filters: string; // JSON string of filters to apply
}

interface FilterAlternativesProps {
  unmappedFilter?: string;
  unmappedValue?: string;
  alternatives: FilterAlternative[];
  onSelect: (filters: Record<string, any>, description: string) => void;
}

export const FilterAlternatives: React.FC<FilterAlternativesProps> = ({
  unmappedFilter,
  unmappedValue,
  alternatives,
  onSelect,
}) => {
  const handleClick = (alt: FilterAlternative) => {
    try {
      const filters = JSON.parse(alt.filters);
      onSelect(filters, alt.description);
    } catch (e) {
      console.error('Failed to parse filters:', e);
    }
  };

  // Determine heading based on context
  const heading = unmappedValue
    ? `Alternative Options for "${unmappedValue}"`
    : `Alternative Search Options`;

  const subtext = unmappedValue
    ? `This exact combination isn't available, but I found these alternatives:`
    : `Here are some related searches that have products:`;

  return (
    <div className="filter-alternatives border border-teal-200 dark:border-teal-700 rounded-lg p-4 my-4 bg-teal-50 dark:bg-teal-900/20 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-teal-600 dark:text-teal-400" />
        <h4 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">
          {heading}
        </h4>
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 ml-6">
        {subtext}
      </p>
      <div className="space-y-2">
        {alternatives.map((alt) => (
          <button
            key={alt.id}
            onClick={() => handleClick(alt)}
            className="w-full text-left p-3 bg-white dark:bg-gray-800 hover:bg-teal-50 dark:hover:bg-teal-900/30 border border-teal-200 dark:border-teal-700 rounded-md transition-all duration-150 hover:shadow-md hover:border-teal-400 dark:hover:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 group"
            aria-label={`Show ${alt.count} products: ${alt.description}`}
          >
            <div className="flex justify-between items-center gap-3">
              <span className="text-sm text-gray-800 dark:text-gray-200 flex-1 pr-2 group-hover:text-teal-700 dark:group-hover:text-teal-300 font-medium">
                {alt.description}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs bg-teal-600 dark:bg-teal-500 text-white px-2 py-1 rounded-full whitespace-nowrap font-medium">
                  {alt.count} {alt.count === 1 ? 'product' : 'products'}
                </span>
                <ArrowRight className="w-4 h-4 text-teal-600 dark:text-teal-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 ml-6 italic">
        Click any option to see those products
      </p>
    </div>
  );
};

export default FilterAlternatives;

